import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar,
  Video,
  FileText,
  Clock,
  Star,
  ArrowRight,
  Plus,
  AlertCircle,
  TrendingUp,
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { sessionsApi } from '../../lib/api/sessions'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

// ── Types ──────────────────────────────────────────────────────────────────────

interface DashboardStats {
  upcomingSessions: number
  completedSessions: number
  feedbackReports: number
}

interface UpcomingInterview {
  source: 'booking' | 'session'
  source_id: number
  title: string
  interviewer_alias: string | null
  interviewer_id: number | null
  scheduled_at: string
  duration_minutes: number
  status: string
  zoom_join_url: string | null
  zoom_password: string | null
}

interface FeedbackItem {
  id: number
  session_title: string
  delivered_at?: string
  created_at?: string
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  const d = new Date(iso)
  return {
    day: d.getDate(),
    month: d.toLocaleString('default', { month: 'short' }).toUpperCase(),
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    weekday: d.toLocaleString('default', { weekday: 'short' }),
    full: d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
  }
}

function isJoinable(iso: string): boolean {
  const scheduled = new Date(iso).getTime()
  const now = Date.now()
  // Allow joining 15 min before and up to 2 hours after scheduled time
  return now >= scheduled - 15 * 60 * 1000 && now <= scheduled + 2 * 60 * 60 * 1000
}

const STATUS_LABEL: Record<string, string> = {
  confirmed: 'Confirmed',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  pending_payment: 'Awaiting Payment',
}
const STATUS_COLOR: Record<string, string> = {
  confirmed: '#16a34a',
  scheduled: '#2563eb',
  in_progress: '#7c3aed',
  pending_payment: '#d97706',
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function ClientHome() {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [stats, setStats] = useState<DashboardStats>({
    upcomingSessions: 0,
    completedSessions: 0,
    feedbackReports: 0,
  })
  const [upcoming, setUpcoming] = useState<UpcomingInterview[]>([])
  const [feedback, setFeedback] = useState<FeedbackItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [upcomingData, completedData, feedbackData] = await Promise.all([
          sessionsApi.getClientInterviews('upcoming').catch(() => []),
          sessionsApi.getClientInterviews('completed').catch(() => []),
          sessionsApi.getFeedback().catch(() => []),
        ])

        const upcomingArr = Array.isArray(upcomingData) ? upcomingData : []
        const completedArr = Array.isArray(completedData) ? completedData : []
        const feedbackArr = Array.isArray(feedbackData) ? feedbackData : []

        setStats({
          upcomingSessions: upcomingArr.length,
          completedSessions: completedArr.length,
          feedbackReports: feedbackArr.length,
        })

        setUpcoming(upcomingArr)
        setFeedback(feedbackArr.slice(0, 3))
      } catch (err) {
        console.error('Dashboard load error:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) return <LoadingSpinner message="Loading dashboard..." />

  const firstName = user?.name?.split(' ')[0] || 'there'

  return (
    <div className="ch-page">
      {/* ── Page Header ─────────────────────────────────────────────────── */}
      <div className="ch-header">
        <div>
          <h1 className="page-title">Welcome back, {firstName}!</h1>
          <p className="page-subtitle">
            Track your progress and prepare for your next mock interview.
          </p>
        </div>
      </div>


      {/* ── Main Grid ───────────────────────────────────────────────────── */}
      <div className="ch-grid">

        {/* ── Upcoming Interviews ──────────────────────────────────────── */}
        <div className="ch-main">
          <div className="card">
            <div className="ch-section-header">
              <div className="ch-section-title">
                <Calendar size={20} />
                <h2>Upcoming Interviews</h2>
                {upcoming.length > 0 && (
                  <span className="ch-count-badge">{upcoming.length}</span>
                )}
              </div>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => navigate('/client/interviews')}
              >
                View All
                <ArrowRight size={14} />
              </button>
            </div>

            {upcoming.length === 0 ? (
              <div className="ch-empty">
                <Calendar size={40} strokeWidth={1.5} />
                <p>No upcoming interviews scheduled.</p>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/client/schedule')}
                >
                  <Plus size={16} />
                  Schedule Your First Interview
                </button>
              </div>
            ) : (
              <div className="ch-interview-list">
                {upcoming.map((item) => {
                  const { day, month, time, weekday } = fmtDate(item.scheduled_at)
                  const statusColor = STATUS_COLOR[item.status] || '#6b7280'
                  const statusLabel = STATUS_LABEL[item.status] || item.status
                  const canJoin = isJoinable(item.scheduled_at)
                  const hasZoom = !!item.zoom_join_url

                  return (
                    <div key={`${item.source}-${item.source_id}`} className="ch-interview-card">
                      {/* Date Badge */}
                      <div className="ch-iv-date">
                        <div className="ch-date-box">
                          <span className="ch-date-weekday">{weekday}</span>
                          <span className="ch-date-day">{day}</span>
                          <span className="ch-date-month">{month}</span>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="ch-iv-details">
                        <div className="ch-iv-top">
                          <h3 className="ch-iv-title">{item.title}</h3>
                          <span
                            className="ch-iv-status"
                            style={{ background: `${statusColor}18`, color: statusColor }}
                          >
                            {statusLabel}
                          </span>
                        </div>

                        <div className="ch-iv-meta">
                          <span><Clock size={13} />{time}</span>
                          <span>&middot;</span>
                          <span>{item.duration_minutes} minutes</span>
                        </div>

                        {item.interviewer_alias && (
                          <div className="ch-iv-interviewer">
                            <div className="ch-iv-avatar">
                              {item.interviewer_alias.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="ch-iv-alias">{item.interviewer_alias}</p>
                              <p className="ch-iv-role">Interview Coach</p>
                            </div>
                          </div>
                        )}

                        {hasZoom && (
                          <div className="ch-zoom-ready">
                            <Video size={13} />
                            <span>Zoom Ready</span>
                            {item.zoom_password && (
                              <span className="ch-zoom-pwd">
                                &nbsp;· Password: <strong>{item.zoom_password}</strong>
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="ch-iv-actions">
                        {hasZoom ? (
                          <button
                            className={`btn ${canJoin ? 'btn-primary' : 'btn-outline'} ch-join-btn`}
                            onClick={() => navigate(`/client/interviews/${item.source}/${item.source_id}`)}
                          >
                            <Video size={15} />
                            {canJoin ? 'Join Now' : 'Join Interview'}
                          </button>
                        ) : (
                          <button className="btn btn-outline ch-join-btn" disabled>
                            <Video size={15} />
                            Join Interview
                          </button>
                        )}
                        <button
                          className="btn btn-outline btn-sm"
                          onClick={() => navigate('/client/interviews')}
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  )
                })}

                {/* Schedule More CTA */}
                <div className="ch-schedule-cta">
                  <button
                    className="btn btn-secondary"
                    onClick={() => navigate('/client/schedule')}
                  >
                    <Plus size={15} />
                    Schedule Another Session
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Right Sidebar ─────────────────────────────────────────────── */}
        <div className="ch-sidebar">

          {/* Progress Card */}
          <div className="card ch-progress-card">
            <div className="ch-section-header">
              <div className="ch-section-title">
                <TrendingUp size={18} />
                <h2>Your Progress</h2>
              </div>
            </div>

            <p className="ch-progress-desc">
              Each completed session builds confidence and sharpens your answers — getting
              you closer to landing your target role.
            </p>

            <div className="ch-progress-rings">
              <div className="ch-ring-item">
                <div className="ch-ring ch-ring--completed">
                  <span>{stats.completedSessions}</span>
                </div>
                <div className="ch-ring-label">
                  <strong>Completed Sessions</strong>
                  <span>Sessions you've finished with full feedback</span>
                </div>
              </div>
              <div className="ch-ring-item">
                <div className="ch-ring ch-ring--upcoming">
                  <span>{stats.upcomingSessions}</span>
                </div>
                <div className="ch-ring-label">
                  <strong>Upcoming Sessions</strong>
                  <span>Interviews you have booked and ready to join</span>
                </div>
              </div>
              <div className="ch-ring-item">
                <div className="ch-ring ch-ring--feedback">
                  <span>{stats.feedbackReports}</span>
                </div>
                <div className="ch-ring-label">
                  <strong>Feedback Reports</strong>
                  <span>Actionable insights from your interviewers</span>
                </div>
              </div>
            </div>

            <button
              className="btn btn-primary ch-progress-btn"
              onClick={() => navigate('/client/schedule')}
            >
              <Calendar size={15} />
              Schedule a Session
            </button>
          </div>

          {/* Recent Feedback Card */}
          <div className="card">
            <div className="ch-section-header">
              <div className="ch-section-title">
                <Star size={18} />
                <h2>Recent Feedback</h2>
              </div>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => navigate('/client/feedback')}
              >
                View All
              </button>
            </div>

            {feedback.length === 0 ? (
              <div className="ch-no-feedback">
                <AlertCircle size={28} strokeWidth={1.5} />
                <p>No feedback yet. Complete an interview to receive insights.</p>
              </div>
            ) : (
              <div className="ch-feedback-list">
                {feedback.map((item) => (
                  <div
                    key={item.id}
                    className="ch-feedback-item"
                    onClick={() => navigate('/client/feedback')}
                  >
                    <div className="ch-fb-icon">
                      <FileText size={16} />
                    </div>
                    <div className="ch-fb-info">
                      <p className="ch-fb-title">{item.session_title}</p>
                      <p className="ch-fb-date">
                        {item.delivered_at
                          ? new Date(item.delivered_at).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                            })
                          : 'Recent'}
                      </p>
                    </div>
                    <ArrowRight size={16} className="ch-fb-arrow" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Tips */}
          <div className="card ch-tips-card">
            <div className="ch-section-title" style={{ marginBottom: 16 }}>
              <h2 style={{ fontSize: 16, fontWeight: 600 }}>Interview Tips</h2>
            </div>
            <ul className="ch-tips-list">
              <li>Review the job description thoroughly before your session</li>
              <li>Prepare 2–3 questions to ask your interviewer</li>
              <li>Test your video and audio 10 minutes beforehand</li>
              <li>Join 5 minutes early to settle in</li>
              <li>Use the STAR method for behavioral questions</li>
            </ul>
          </div>
        </div>
      </div>

      <style>{styles}</style>
    </div>
  )
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const styles = `
  .ch-page {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  /* Header */
  .ch-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .ch-schedule-btn {
    flex-shrink: 0;
    margin-top: 4px;
  }

  /* Main Grid */
  .ch-grid {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 24px;
    align-items: start;
  }
  .ch-main { display: flex; flex-direction: column; gap: 0; }
  .ch-sidebar { display: flex; flex-direction: column; gap: 20px; }

  /* Section headers */
  .ch-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
  }
  .ch-section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--primary-navy);
  }
  .ch-section-title h2 {
    font-size: 17px;
    font-weight: 600;
  }
  .ch-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    background: var(--primary-navy);
    color: white;
    border-radius: 11px;
    font-size: 12px;
    font-weight: 700;
  }

  /* Upcoming interviews list */
  .ch-interview-list {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .ch-interview-card {
    display: flex;
    gap: 20px;
    align-items: flex-start;
    padding: 18px;
    background: var(--accent-light, #f8f9fa);
    border-radius: 14px;
    border: 1px solid var(--border);
    transition: box-shadow 0.2s, border-color 0.2s;
  }
  .ch-interview-card:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
    border-color: var(--primary-navy);
  }

  .ch-iv-date { flex-shrink: 0; }
  .ch-date-box {
    width: 64px;
    height: 72px;
    background: var(--primary-navy);
    color: var(--accent-cream, #f5f0dc);
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1px;
  }
  .ch-date-weekday { font-size: 10px; font-weight: 500; opacity: 0.8; text-transform: uppercase; }
  .ch-date-day     { font-size: 26px; font-weight: 700; line-height: 1; }
  .ch-date-month   { font-size: 11px; font-weight: 500; }

  .ch-iv-details { flex: 1; min-width: 0; }
  .ch-iv-top {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 6px;
    flex-wrap: wrap;
  }
  .ch-iv-title {
    font-size: 16px;
    font-weight: 600;
    flex: 1;
  }
  .ch-iv-status {
    font-size: 11px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 5px;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .ch-iv-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--text-muted);
    font-size: 13px;
    margin-bottom: 10px;
  }
  .ch-iv-meta span { display: flex; align-items: center; gap: 4px; }

  .ch-iv-interviewer {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    background: white;
    border-radius: 10px;
    width: fit-content;
    margin-bottom: 8px;
  }
  .ch-iv-avatar {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--primary-navy);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
  }
  .ch-iv-alias { font-size: 13px; font-weight: 600; }
  .ch-iv-role  { font-size: 11px; color: var(--text-muted); }

  .ch-zoom-ready {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #16a34a;
    padding: 4px 10px;
    background: rgba(22,163,74,0.08);
    border-radius: 6px;
  }
  .ch-zoom-pwd { color: var(--text-muted); }

  .ch-iv-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-end;
    flex-shrink: 0;
  }
  .ch-join-btn {
    min-width: 130px;
    justify-content: center;
    text-decoration: none;
  }
  .btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .ch-schedule-cta {
    display: flex;
    justify-content: center;
    padding-top: 4px;
  }

  /* Empty state */
  .ch-empty {
    text-align: center;
    padding: 48px 24px;
    color: var(--text-muted);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
  }
  .ch-empty p { font-size: 15px; }

  /* Progress Card */
  .ch-progress-card {}
  .ch-progress-desc {
    font-size: 13px;
    color: var(--text-muted);
    line-height: 1.6;
    margin-bottom: 20px;
    padding: 10px 14px;
    background: var(--accent-light, #f8f9fa);
    border-left: 3px solid var(--primary-navy);
    border-radius: 0 8px 8px 0;
  }
  .ch-progress-rings {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 20px;
  }
  .ch-ring-item {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .ch-ring {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    border: 3px solid var(--primary-navy);
    background: var(--accent-light, #f8f9fa);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ch-ring span {
    font-size: 20px;
    font-weight: 700;
    color: var(--primary-navy);
  }
  .ch-ring--upcoming {
    background: var(--primary-navy);
  }
  .ch-ring--upcoming span { color: white; }
  .ch-ring--feedback {
    border-color: #22c55e;
  }
  .ch-ring--feedback span { color: #16a34a; }
  .ch-ring-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .ch-ring-label strong { font-size: 14px; font-weight: 600; color: var(--text-dark); }
  .ch-ring-label span   { font-size: 12px; color: var(--text-muted); line-height: 1.4; }
  .ch-progress-btn {
    width: 100%;
    justify-content: center;
  }

  /* Feedback */
  .ch-no-feedback {
    text-align: center;
    color: var(--text-muted);
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    font-size: 13px;
  }
  .ch-feedback-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .ch-feedback-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: var(--accent-light, #f8f9fa);
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s;
  }
  .ch-feedback-item:hover { background: #edf2f7; }
  .ch-fb-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: rgba(45,55,72,0.08);
    color: var(--primary-navy);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ch-fb-info { flex: 1; min-width: 0; }
  .ch-fb-title {
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ch-fb-date { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .ch-fb-arrow { color: var(--text-muted); flex-shrink: 0; }

  /* Tips */
  .ch-tips-list {
    list-style: none;
    display: flex;
    flex-direction: column;
  }
  .ch-tips-list li {
    font-size: 13px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: flex-start;
    gap: 10px;
    line-height: 1.5;
  }
  .ch-tips-list li:last-child { border-bottom: none; }
  .ch-tips-list li::before {
    content: "✓";
    color: var(--success, #48bb78);
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 1px;
  }

  /* Responsive */
  @media (max-width: 1100px) {
    .ch-grid {
      grid-template-columns: 1fr;
    }
    .ch-sidebar {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }
  }
  @media (max-width: 768px) {
    .ch-stats { grid-template-columns: 1fr; }
    .ch-sidebar { grid-template-columns: 1fr; }
    .ch-header { flex-direction: column; }
    .ch-schedule-btn { width: 100%; justify-content: center; }
    .ch-interview-card { flex-direction: column; }
    .ch-iv-actions { flex-direction: row; width: 100%; }
    .ch-join-btn { flex: 1; }
  }
  @media (max-width: 480px) {
    .ch-stats { grid-template-columns: 1fr; }
  }
`
